import { useT } from '@/lib/i18n/useT';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import type { PlanResult } from './engine';

type Measured = Extract<PlanResult, { state: 'MEASURED' }>;

/** Pares fondo / tinta de la barra del reparto (D-15): colores de marca ya existentes, sin hex nuevo. */
const PALETTE: Array<{ bg: string; ink: string }> = [
  { bg: 'var(--color-dash-lime)', ink: 'var(--color-dash-ink)' },
  { bg: 'var(--color-dash-ink)', ink: 'var(--color-xk-surface)' },
  { bg: 'var(--color-dash-olive)', ink: 'var(--color-xk-surface)' },
  { bg: 'var(--color-dash-amber)', ink: 'var(--color-dash-ink)' },
  { bg: 'var(--color-dash-teal)', ink: 'var(--color-xk-surface)' },
  { bg: 'var(--color-dash-gray)', ink: 'var(--color-dash-ink)' },
  { bg: 'var(--color-dash-red)', ink: 'var(--color-xk-surface)' },
];

export interface RarityNote {
  /** Hay filas con rareza (no sólo cadencia). */
  hasWeight: boolean;
  /** Minutos entre premios, si alguna fila va por cadencia. */
  cooldown: number | null;
}

interface OrderSplitProps {
  result: Measured;
  /** Con evento, «para este evento» en vez de «por evento». */
  eventMode: boolean;
  /** Cadencia por merch (sólo con evento), para no llamar «muy rara» a lo que sale por tiempo. */
  notes?: ReadonlyMap<number, RarityNote>;
}

/** El peso (6 − rareza)² de vuelta a rareza 1–5. */
const ratingOf = (weight: number): number => Math.max(1, Math.min(5, Math.round(6 - Math.sqrt(weight))));

/** La tarjeta oscura del pedido y el reparto por merch (barra partida + tabla). */
export function OrderSplit({ result, eventMode, notes }: OrderSplitProps) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const fmt = (n: number) => n.toLocaleString(language);
  const perEvent = Math.ceil(result.toOrder / result.working.events);
  const single = eventMode || result.working.events === 1;

  const rarityText = (stockId: number, min: number, max: number): string => {
    const note = notes?.get(stockId);
    const parts: string[] = [];
    if (!note || note.hasWeight) {
      // El peso mayor es la rareza menor: de más común a más rara.
      const a = ratingOf(max);
      const b = ratingOf(min);
      parts.push(a === b ? t(`merchPlanner.rarityWord.${a}`) : `${t(`merchPlanner.rarityWord.${a}`)}–${t(`merchPlanner.rarityWord.${b}`)}`);
    }
    if (note?.cooldown != null && note.cooldown > 0) parts.push(t('merchPlanner.cooldown', { n: note.cooldown }));
    return parts.join(' · ');
  };

  return (
    <>
      <div
        data-surface="night"
        data-testid="planner-order-total"
        className="flex flex-col gap-6 rounded-[18px] bg-[var(--color-xk-bg-nav)] p-6 text-[var(--color-fg-primary)] sm:flex-row sm:items-center"
      >
        <div className="flex flex-col gap-1">
          <span className="text-[13px] uppercase tracking-[1px] text-[var(--color-fg-muted)]">{t('merchPlanner.result.order')}</span>
          <span className="font-[var(--font-dash-mono)] text-[64px] font-bold leading-none tabular-nums text-[var(--color-accent-ink)]">
            {fmt(result.toOrder)}
          </span>
          <span className="text-sm text-[var(--color-fg-primary)]">
            {single ? t('merchPlanner.result.piecesOne') : t('merchPlanner.result.piecesPerEvent', { n: fmt(perEvent) })}
          </span>
        </div>
        <div className="grid flex-1 grid-cols-3 gap-2.5">
          {[
            ['merchPlanner.result.players', `${fmt(result.players)}`],
            ['merchPlanner.result.claims', `${fmt(result.claims)}`],
            ['merchPlanner.result.buffer', `+${fmt(result.leftovers)}`],
          ].map(([k, v]) => (
            <div key={k} className="flex flex-col gap-1 rounded-xl bg-[var(--color-bg-elevated)] p-3.5">
              <span className="text-xs text-[var(--color-fg-muted)]">{t(k)}</span>
              <span className="font-[var(--font-dash-mono)] text-[22px] font-bold tabular-nums">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-[18px] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-bold text-[var(--color-fg-primary)]">{t('merchPlanner.split.title')}</h2>
          <span className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.split.hint')}</span>
        </div>
        <div className="mt-4 flex h-10 gap-[3px] overflow-hidden rounded-[10px]" aria-hidden="true" data-testid="planner-split-bar">
          {result.rows.map((r, i) => {
            const c = PALETTE[i % PALETTE.length];
            return (
              <div
                key={r.stockId}
                style={{ width: `${(r.chance * 100).toFixed(2)}%`, background: c.bg, color: c.ink }}
                className="flex items-center justify-center font-[var(--font-dash-mono)] text-[13px] font-bold"
              >
                {r.chance >= 0.08 ? `${Math.round(r.chance * 100)}%` : null}
              </div>
            );
          })}
        </div>
        <table className="mt-3 w-full text-sm">
          <thead className="border-b border-[var(--color-border-subtle)] text-left text-xs text-[var(--color-fg-muted)]">
            <tr>
              <th className="py-2 font-normal">{t('merchPlanner.col.merch')}</th>
              <th className="py-2 font-normal">{t('merchPlanner.col.rarity')}</th>
              <th className="py-2 text-right font-normal">{t('merchPlanner.col.planned')}</th>
              <th className="py-2 text-right font-normal">{t('merchPlanner.col.order')}</th>
              <th className="py-2 text-right font-normal">{t('merchPlanner.col.restock')}</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((r, i) => (
              <tr key={r.stockId} className="border-b border-[var(--color-border-subtle)]" data-testid="calc-row">
                  <td className="py-3">
                    <span className="flex items-center gap-2.5 font-medium">
                      <span
                        aria-hidden="true"
                        style={{ background: PALETTE[i % PALETTE.length].bg }}
                        className="inline-block h-3.5 w-3.5 shrink-0 rounded border border-[var(--color-border-strong)]"
                      />
                      <span className="truncate">{r.name}</span>
                    </span>
                  </td>
                  <td className="py-3 text-[13px] text-[var(--color-fg-muted)]">{rarityText(r.stockId, r.weightMin, r.weightMax)}</td>
                  <td className="py-3 text-right font-[var(--font-dash-mono)] tabular-nums text-[var(--color-fg-muted)]">{fmt(r.planned)}</td>
                  <td className="py-3 text-right font-[var(--font-dash-mono)] text-base font-bold tabular-nums">{fmt(r.toOrder)}</td>
                  <td className="py-3 text-right text-[13px] text-[var(--color-fg-muted)]">
                    {r.restockAt == null ? '—' : t('merchPlanner.restockWhen', { n: r.restockAt })}
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
